import { ColorHarmony } from '@theme/types/colorHarmony.types.ts';
import { hexToHsl, hslToHex, normalizeHex } from '@theme/utils/colorConversions.ts';

const clamp = (value: number, min: number, max: number): number =>
    Math.min(max, Math.max(min, value));

const wrapHue = (hue: number): number => ((hue % 360) + 360) % 360;

const buildColor = (h: number, s: number, l: number): string =>
    hslToHex(wrapHue(h), clamp(s, 0, 100), clamp(l, 0, 100));

export const generateMonochromatic = (baseHex: string, count: 1 | 2 | 3): string[] => {
    const base = normalizeHex(baseHex);
    if (count === 1) {
        return [base];
    }

    const { h, s, l } = hexToHsl(base);
    const light = buildColor(h, s * 0.85, l + 18);

    if (count === 2) {
        return [base, light];
    }

    const dark = buildColor(h, s * 1.05, l - 18);
    return [base, light, dark];
};

export const generateAnalogous = (baseHex: string, count: 1 | 2 | 3): string[] => {
    const base = normalizeHex(baseHex);
    if (count === 1) {
        return [base];
    }

    const { h, s, l } = hexToHsl(base);
    const next = buildColor(h + 30, s, l);

    if (count === 2) {
        return [base, next];
    }

    const prev = buildColor(h - 30, s, l);
    // Return base first to ensure Index 0 corresponds to baseColor1 in the picker logic
    return [base, prev, next];
};

export const generateComplementary = (baseHex: string, count: 1 | 2 | 3): string[] => {
    const base = normalizeHex(baseHex);
    if (count === 1) {
        return [base];
    }

    const { h, s, l } = hexToHsl(base);
    const complement = buildColor(h + 180, s, l);

    if (count === 2) {
        return [base, complement];
    }

    const splitComplement = buildColor(h + 210, s, l);
    return [base, complement, splitComplement];
};

export const generateTriadic = (baseHex: string, count: 1 | 2 | 3): string[] => {
    const base = normalizeHex(baseHex);
    if (count === 1) {
        return [base];
    }

    const { h, s, l } = hexToHsl(base);
    const second = buildColor(h + 120, s, l);

    if (count === 2) {
        return [base, second];
    }

    const third = buildColor(h + 240, s, l);
    return [base, second, third];
};

export const generateHarmony = (
    baseHex: string,
    harmony: ColorHarmony,
    count: 1 | 2 | 3,
): string[] => {
    switch (harmony) {
        case 'monochromatic':
            return generateMonochromatic(baseHex, count);
        case 'analogous':
            return generateAnalogous(baseHex, count);
        case 'complementary':
            return generateComplementary(baseHex, count);
        case 'triadic':
            return generateTriadic(baseHex, count);
        case 'custom':
        default:
            return [normalizeHex(baseHex)];
    }
};
